import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LocalFireDepartment as Scope1Icon,
  PublicOutlined as Scope3Icon,
  People as SocialIcon,
  Nature as EnvironmentIcon,
  WaterDrop as WaterIcon,
  DeleteSweep as WasteIcon,
  WbSunny as RenewableIcon,
  AdminPanelSettings as AdminIcon,
  Science as EmissionFactorIcon,
  AutoAwesome as AIInsightsIcon,
  History as AuditTrailIcon,
  Assignment as GRIIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';

const DRAWER_WIDTH = 220;

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactElement;
  description: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: <DashboardIcon />,
    description: 'ESG Overview',
  },
  {
    id: 'environment',
    label: 'Environment',
    path: '/environment',
    icon: <EnvironmentIcon />,
    description: 'Environmental Data',
    children: [
      {
        id: 'scope12',
        label: 'Scope 1 & 2',
        path: '/scope12',
        icon: <Scope1Icon />,
        description: 'Direct & Energy Emissions',
      },
      {
        id: 'scope3',
        label: 'Scope 3',
        path: '/scope3',
        icon: <Scope3Icon />,
        description: 'Value Chain Emissions',
      },
      {
        id: 'water',
        label: 'Water',
        path: '/water',
        icon: <WaterIcon />,
        description: 'Water Management',
      },
      {
        id: 'waste',
        label: 'Waste',
        path: '/waste',
        icon: <WasteIcon />,
        description: 'Waste Management',
      },
      {
        id: 'renewable',
        label: 'Renewable Energy',
        path: '/renewable',
        icon: <RenewableIcon />,
        description: 'Renewable Sources',
      },
    ],
  },
  {
    id: 'social',
    label: 'Social & People',
    path: '/social',
    icon: <SocialIcon />,
    description: 'Workforce & Safety',
  },
  {
    id: 'insights',
    label: 'AI Insights',
    path: '/insights',
    icon: <AIInsightsIcon />,
    description: 'AI-Powered Recommendations',
  },
  {
    id: 'gri',
    label: 'GRI Reporting',
    path: '/gri',
    icon: <GRIIcon />,
    description: 'GRI Standards Questionnaire',
  },
];

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useApp();
  const [environmentOpen, setEnvironmentOpen] = useState(true);

  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const isEnvironmentPath = (path: string) => {
    return ['/scope12', '/scope3', '/water', '/waste', '/renewable'].some(
      (envPath) => path === envPath || path.startsWith(envPath + '/')
    );
  };

  const renderNavItem = (item: NavItem, isChild = false) => {
    const isActive = location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path));
    const hasChildren = item.children && item.children.length > 0;
    const isEnvironmentActive = item.id === 'environment' && isEnvironmentPath(location.pathname);

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                setEnvironmentOpen(!environmentOpen);
              } else {
                navigate(item.path);
              }
            }}
            sx={{
              borderRadius: 1.5,
              backgroundColor: (isActive || isEnvironmentActive) ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
              '&:hover': {
                backgroundColor: (isActive || isEnvironmentActive)
                  ? 'rgba(59, 130, 246, 0.2)'
                  : 'rgba(255,255,255,0.05)',
              },
              py: isChild ? 0.5 : 0.75,
              px: 1.5,
              pl: isChild ? 3 : 1.5,
            }}
          >
            <ListItemIcon
              sx={{
                color: (isActive || isEnvironmentActive) ? '#3b82f6' : 'rgba(255,255,255,0.6)',
                minWidth: isChild ? 28 : 32,
                '& .MuiSvgIcon-root': { fontSize: isChild ? 18 : 20 },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: (isActive || isEnvironmentActive) ? 600 : 400,
                color: (isActive || isEnvironmentActive) ? '#fff' : 'rgba(255,255,255,0.85)',
                fontSize: isChild ? '0.75rem' : '0.8rem',
              }}
            />
            {hasChildren && (
              environmentOpen ? (
                <ExpandLess sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
              ) : (
                <ExpandMore sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
              )
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={environmentOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderNavItem(child, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#1e293b',
          color: '#fff',
          borderRight: 'none',
        },
      }}
    >
      {/* Logo/Brand */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {/* Organization Logo */}
          <svg width="24" height="28" viewBox="0 0 41 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.3308 16.3848L22.6978 23.6676H27.6298C27.6104 23.6093 27.5877 23.5542 27.5682 23.499L27.552 23.5088C22.4222 10.4671 23.155 0 23.155 0H17.1368C17.1368 0 17.8696 10.4671 12.7431 23.5055L12.7269 23.4958C12.7074 23.5542 12.6847 23.6093 12.6653 23.6644H17.9637L20.3308 16.3815V16.3848Z" fill="#cb423a"/>
            <path d="M28.2137 25.2208L24.1605 28.1683L27.8506 35.1594L20.331 30.9505L13.1584 35.1594L16.5015 28.1683L12.1597 25.0165C7.26015 37.6886 0 44.0181 0 44.0181L3.9819 48C3.9819 48 9.76019 37.7048 20.1365 38.0712C30.5127 37.7956 36.3105 48 36.3105 48L40.2924 44.0181C40.2924 44.0181 33.1133 37.7567 28.2137 25.2241V25.2208Z" fill="#cb423a"/>
          </svg>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.85rem' }}>
            {user?.organizationName || 'ESG Platform'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>
            ESG Portal
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1.5 }} />

      {/* Navigation Items */}
      <List sx={{ px: 1.5, pt: 1.5 }}>
        {navItems.map((item) => renderNavItem(item))}
      </List>

      {/* Admin Section - Only visible to admins */}
      {isAdmin && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1.5, my: 0.5 }} />
          <List sx={{ px: 1.5 }}>
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => navigate('/admin')}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: location.pathname === '/admin' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                  '&:hover': {
                    backgroundColor: location.pathname === '/admin'
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(255,255,255,0.05)',
                  },
                  py: 0.75,
                  px: 1.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === '/admin' ? '#3b82f6' : 'rgba(255,255,255,0.6)',
                    minWidth: 32,
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Administration"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: location.pathname === '/admin' ? 600 : 400,
                    color: location.pathname === '/admin' ? '#fff' : 'rgba(255,255,255,0.85)',
                    fontSize: '0.8rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => navigate('/admin/emission-factors')}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: location.pathname === '/admin/emission-factors' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                  '&:hover': {
                    backgroundColor: location.pathname === '/admin/emission-factors'
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(255,255,255,0.05)',
                  },
                  py: 0.75,
                  px: 1.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === '/admin/emission-factors' ? '#3b82f6' : 'rgba(255,255,255,0.6)',
                    minWidth: 32,
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  <EmissionFactorIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Emission Factors"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: location.pathname === '/admin/emission-factors' ? 600 : 400,
                    color: location.pathname === '/admin/emission-factors' ? '#fff' : 'rgba(255,255,255,0.85)',
                    fontSize: '0.8rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => navigate('/admin/audit-trail')}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: location.pathname === '/admin/audit-trail' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                  '&:hover': {
                    backgroundColor: location.pathname === '/admin/audit-trail'
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(255,255,255,0.05)',
                  },
                  py: 0.75,
                  px: 1.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === '/admin/audit-trail' ? '#3b82f6' : 'rgba(255,255,255,0.6)',
                    minWidth: 32,
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  <AuditTrailIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Audit Trail"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: location.pathname === '/admin/audit-trail' ? 600 : 400,
                    color: location.pathname === '/admin/audit-trail' ? '#fff' : 'rgba(255,255,255,0.85)',
                    fontSize: '0.8rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}

      {/* Footer Info */}
      <Box sx={{ mt: 'auto', p: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', fontSize: '0.65rem' }}>
          Version 1.0.0
        </Typography>
      </Box>
    </Drawer>
  );
};
